import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Palette, Sparkles, Calculator, Library, ArrowRight } from "lucide-react";

const TOOLS = [
  {
    to: "/color-quiz",
    icon: Palette,
    title: "Color Quiz",
    eyebrow: "14 Questions · 3 Curated Picks",
    caption:
      "Answer 14 short questions and receive three verified color recommendations — a Best Match, a Safe Pick and a Designer Pick — with reasons, undertones and Est. LRV.",
    testid: "free-tool-color-quiz",
  },
  {
    to: "/sheen-quiz",
    icon: Sparkles,
    title: "Sheen Guide",
    eyebrow: "Match Finish To The Job",
    caption:
      "Flat, Eggshell, Satin, Semi-Gloss, High-Gloss — learn what each finish does, then take the quiz to find the ideal sheen for your surface, room and lifestyle.",
    testid: "free-tool-sheen",
  },
  {
    to: "/colors",
    icon: Library,
    title: "Color Library",
    eyebrow: "5,644 Verified Colors",
    caption:
      "Search the full official Sherwin-Williams and Benjamin Moore libraries. Filter by family, undertone, depth and LRV. Save favorites to your portal.",
    testid: "free-tool-library",
  },
  {
    to: "/paint-calculator",
    icon: Calculator,
    title: "Paint Calculator",
    eyebrow: "How Much Paint Do I Need?",
    caption:
      "Enter your room dimensions and coats — we return exact gallons, paintable square feet and a bucket-plus-can purchase breakdown.",
    testid: "free-tool-calculator",
  },
];

export function FreeTools() {
  return (
    <div className="bg-ivory pt-28 pb-24">
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.p initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{duration:.5}} className="eyebrow text-gold">Free Tools</motion.p>
          <motion.h1 initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{duration:.6,delay:.05}} className="font-serif text-4xl md:text-6xl mt-3 text-ink leading-[1.05] max-w-3xl">
            Everything you need to plan the perfect paint project.
          </motion.h1>
          <p className="mt-6 max-w-2xl text-ink/70 md:text-lg">
            Four designer-built tools to make color, finish and quantity decisions easy: pick the right color, match the right finish, browse verified libraries and calculate exactly how much paint you'll need — all free, no account required.
          </p>
          <div className="mt-6 h-px w-24 bg-gold" />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 mt-16 grid md:grid-cols-2 gap-6" data-testid="free-tools-grid">
        {TOOLS.map((t, i) => (
          <motion.div
            key={t.to}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link to={t.to} data-testid={t.testid} className="block h-full bg-white border border-black/10 p-8 md:p-10 group hover:border-gold transition-colors">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 border border-black/10 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-white transition-colors">
                  <t.icon size={20} />
                </div>
                <ArrowRight className="text-ink/30 group-hover:text-gold transition-colors" size={20} />
              </div>
              <p className="mt-8 overline text-[10px] text-ink/50">{t.eyebrow}</p>
              <p className="mt-2 font-serif text-3xl leading-tight">{t.title}</p>
              <p className="mt-4 text-sm text-ink/70 leading-relaxed">{t.caption}</p>
              <p className="mt-6 text-xs text-gold overline">Open Tool →</p>
            </Link>
          </motion.div>
        ))}
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-12 mt-20">
        <div className="bg-ink text-white p-10 md:p-14 grid md:grid-cols-[2fr_1fr] items-center gap-8">
          <div>
            <p className="eyebrow text-gold">The Design Portal</p>
            <p className="font-serif text-3xl md:text-4xl mt-2">Save your results. Organize by home.</p>
            <p className="mt-4 text-white/70 max-w-xl">Create a free account to save quiz results, favorite colors and palettes to each property in <b>My Homes</b> — plus keep a paint history for every room.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/signup" data-testid="free-tools-signup-cta" className="bg-gold text-white overline px-8 py-4 hover:bg-gold-dark transition-colors">Create My Free Portal</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
